import { SORT_DIRECTIONS } from "renderers/contributors-table/helpers"

/**
 * Get the sort cycle for a column based on its type
 * @param {boolean} isTextColumn - Whether the column is a text column
 * @returns {Array} Array of sort directions in cycle order, including null for "unsort"
 */
export function getSortCycle(isTextColumn) {
  return isTextColumn
    ? [SORT_DIRECTIONS.ASC, SORT_DIRECTIONS.DESC, null]
    : [SORT_DIRECTIONS.DESC, SORT_DIRECTIONS.ASC, SORT_DIRECTIONS.DESC_ABS, SORT_DIRECTIONS.ASC_ABS, null]
}

/**
 * Get all available sort options for a column (excluding null/unsort)
 * @param {boolean} isTextColumn - Whether the column is a text column
 * @returns {Array} Array of sort option objects with direction, label, and icon
 */
export function getSortOptions(isTextColumn) {
  if (isTextColumn) {
    return [
      { direction: SORT_DIRECTIONS.ASC, label: "Sort A-Z", icon: "asc" },
      { direction: SORT_DIRECTIONS.DESC, label: "Sort Z-A", icon: "desc" },
    ]
  }

  return [
    { direction: SORT_DIRECTIONS.DESC, label: "Sort positive to negative", icon: "desc" },
    { direction: SORT_DIRECTIONS.ASC, label: "Sort negative to positive", icon: "asc" },
    { direction: SORT_DIRECTIONS.DESC_ABS, label: "Sort high to low magnitude", icon: "desc" },
    { direction: SORT_DIRECTIONS.ASC_ABS, label: "Sort low to high magnitude", icon: "asc" },
  ]
}

/**
 * Get the next sort direction in the cycle
 * @param {Object} params - Parameters object
 * @param {boolean} params.isSortedColumn - Whether this column is currently sorted
 * @param {boolean} params.isTextColumn - Whether this is a text column
 * @param {boolean} params.isAbsolute - Whether current sort is absolute
 * @param {string} params.sortArrowDirection - Current sort arrow direction ("asc" or "desc")
 * @returns {string|null} Next sort direction or null for unsort
 */
export function getNextSortDirection({ isSortedColumn, isTextColumn, isAbsolute, sortArrowDirection }) {
  const sortCycle = getSortCycle(isTextColumn)

  if (!isSortedColumn) {
    return sortCycle[0]
  }

  const currentSortDirection = isAbsolute
    ? (sortArrowDirection === "desc" ? SORT_DIRECTIONS.DESC_ABS : SORT_DIRECTIONS.ASC_ABS)
    : (sortArrowDirection === "desc" ? SORT_DIRECTIONS.DESC : SORT_DIRECTIONS.ASC)

  const currentIndex = sortCycle.indexOf(currentSortDirection)
  const nextIndex = (currentIndex + 1) % sortCycle.length
  return sortCycle[nextIndex]
}

/**
 * Check if a sort direction is absolute
 * @param {string} direction - Sort direction constant
 * @returns {boolean} Whether the direction is absolute
 */
export function isAbsoluteSort(direction) {
  return direction === SORT_DIRECTIONS.ASC_ABS || direction === SORT_DIRECTIONS.DESC_ABS
}

/**
 * Get the arrow direction from a sort direction
 * @param {string} direction - Sort direction constant
 * @returns {string} "asc" or "desc"
 */
export function getArrowDirection(direction) {
  return direction === SORT_DIRECTIONS.ASC || direction === SORT_DIRECTIONS.ASC_ABS ? "asc" : "desc"
}
